import React, { FunctionComponent, useEffect, useState, useCallback, useMemo } from 'react';
import { CalculatorAdditionalPaymentStepper } from './stepper';
import { useFormContext, useStepper } from '../../../../hooks';
import { CalculatorAdditionalPaymentApiResponse } from './types';
import useMediaQuery from '../../../../hooks/use-media';
import { mainMediaMobile } from '../../../../utils';
import { AppAlertDialog } from '../../../shared/general';
import { Box } from '@chakra-ui/react';
import { FormattedMessage, IntlProvider, MessageFormatElement } from 'react-intl';
import { API_ASSETS } from '../../../../services';
import { sharedUtilsGetLang } from '../../../../utils';
import { CalculatorAdditionalPaymentCta } from './calculator-additional-payment-cta';
import { CalculatorAdditionalPaymentResult } from './calculator-additional-payment-result';

const locale = sharedUtilsGetLang();

export const CalculatorAdditionalPayment: FunctionComponent = () => {
  const { result, hasError, setError, updateResult } =
    useFormContext<CalculatorAdditionalPaymentApiResponse>();
  const { currentStep, setStepActive } = useStepper();
  const [messages, setMessages] = useState<Record<string, MessageFormatElement[]>>(
    {} as Record<string, MessageFormatElement[]>
  );
  const isMobile = useMediaQuery(mainMediaMobile);
  const [loading, setLoading] = useState(true);

  const handleOnClose = useCallback(() => {
    updateResult(null);
    setError(false);
    setStepActive(0, true);
  }, [updateResult, setError, setStepActive]);

  const ctaClassName = useMemo(() => {
    if (!currentStep) return 'col-span-12 md:col-span-5';
    return currentStep > 0 && isMobile
      ? 'col-span-12 md:col-span-5 mb-8'
      : 'col-span-12 md:col-span-5';
  }, [currentStep, isMobile]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const messagesData: Record<string, MessageFormatElement[]> = await API_ASSETS.get(
          `calculators/additional-payment/compiled-lang/${locale}.json`
        );
        setMessages(messagesData);
      } catch (error) {
        console.error('Failed to load data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []); // No need to include `locale` if it's constant

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <IntlProvider locale={locale} messages={messages} defaultLocale={'nl'}>
      <div className={`grid grid-cols-12 p-4 md:p-0`}>
        {hasError && (
          <AppAlertDialog title={'let op!'} open={hasError} onClose={handleOnClose}>
            <Box>
              <FormattedMessage
                id={'default.error'}
                defaultMessage={`Door een technische storing kunnen we nu helaas geen berekening uitvoeren. Probeer het
              opnieuw.`}
              />
            </Box>
          </AppAlertDialog>
        )}
        <div className={ctaClassName}>
          <CalculatorAdditionalPaymentCta></CalculatorAdditionalPaymentCta>
        </div>
        <div className={`col-span-12 md:col-span-7`}>
          <div hidden={!!result}>
            <CalculatorAdditionalPaymentStepper></CalculatorAdditionalPaymentStepper>
          </div>
          {result && <CalculatorAdditionalPaymentResult></CalculatorAdditionalPaymentResult>}
        </div>
      </div>
    </IntlProvider>
  );
};
